import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Loader2,
  History,
  AlertCircle,
  User,
  Shield,
  Clock,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CampaignHistoryPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    fetchData();
  }, [navigate, campaignId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignRes, auditRes] = await Promise.all([
        axios.get(`${API_URL}/api/client-portal/campaigns/${campaignId}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/client-portal/campaigns/${campaignId}/audit`, { headers: getAuthHeaders() })
      ]);
      setCampaign(campaignRes.data);
      setAuditLogs(auditRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Campaign not found');
        navigate('/client-portal/campaigns');
      } else {
        toast.error('Failed to load history');
      }
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'created': return 'bg-green-100 text-green-800';
      case 'updated': return 'bg-blue-100 text-blue-800';
      case 'toggled': return 'bg-orange-100 text-orange-800';
      case 'state_change': return 'bg-purple-100 text-purple-800';
      case 'admin_override': return 'bg-red-100 text-red-800';
      case 'deleted': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="campaign-history-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/client-portal/campaigns/${campaignId}/edit`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <History className="w-5 h-5" />
                Campaign History
              </h1>
              <p className="text-sm text-muted-foreground">{campaign?.name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Campaign Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Campaign Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>
                <p className="font-medium">{campaign?.name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <p><Badge variant={campaign?.is_active ? 'default' : 'secondary'}>{campaign?.is_active ? 'Active' : 'Paused'}</Badge></p>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <p className="font-medium">{formatDate(campaign?.created_at)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Last Updated:</span>
                <p className="font-medium">{formatDate(campaign?.updated_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Audit Log Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Change History ({auditLogs.length} records)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {auditLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No history records found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead className="w-[120px]">Action</TableHead>
                    <TableHead className="w-[200px]">Changed By</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-sm">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionBadgeColor(log.action)}>
                          {log.action.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {log.changed_by?.type === 'admin' ? (
                            <Shield className="w-4 h-4 text-orange-500" />
                          ) : (
                            <User className="w-4 h-4 text-blue-500" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{log.changed_by?.name || log.changed_by?.email || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{log.changed_by?.type}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {log.action === 'toggled' && (
                            <p>
                              Campaign turned <strong>{log.details?.toggled_to}</strong>
                            </p>
                          )}
                          {log.action === 'state_change' && (
                            <div>
                              <p>States updated: <strong>{log.details?.total_states}</strong> selected</p>
                              {log.details?.states_added?.length > 0 && (
                                <p className="text-green-600 text-xs">+ Added: {log.details.states_added.join(', ')}</p>
                              )}
                              {log.details?.states_removed?.length > 0 && (
                                <p className="text-red-600 text-xs">- Removed: {log.details.states_removed.join(', ')}</p>
                              )}
                            </div>
                          )}
                          {log.action === 'admin_override' && (
                            <div className="flex items-center gap-2 text-orange-600">
                              <AlertCircle className="w-4 h-4" />
                              <span>Admin Override - Fields: {log.details?.fields_changed?.join(', ')}</span>
                            </div>
                          )}
                          {log.action === 'created' && (
                            <p>Campaign created</p>
                          )}
                          {log.action === 'updated' && (
                            <p>Campaign settings updated</p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
